import React, { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, Filter, Grid, List, ShoppingCart, Package, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'

const KataleProducts = () => {
  const { customer } = useCustomerAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('grid')
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '')
  const [sortBy, setSortBy] = useState('newest')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalProducts, setTotalProducts] = useState(0)
  const [cartCount, setCartCount] = useState(0)
  const [error, setError] = useState('')
  const [priceRange, setPriceRange] = useState({ min: 0, max: 0 })

  useEffect(() => {
    fetchCategories()
    fetchCartCount()
  }, [customer])

  useEffect(() => {
    fetchProducts()
  }, [selectedCategory, sortBy, currentPage, searchQuery])

  const fetchCategories = async () => {
    try {
      const response = await apiService.getKataleCategories()
      if (response.status === 'SUCCESS' && response.data) {
        setCategories([
          { id: '', name: 'All Categories' },
          ...response.data.map(cat => ({ id: cat.id.toString(), name: cat.name }))
        ])
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err)
    }
  }

  // Helper to extract MG code from tenant DB (e.g., kololo_mg from mgfina6_tenant_kololo_mg)
  const getMgCode = (tenantDb) => {
    if (!tenantDb) return null
    const prefix = 'mgfina6_tenant_'
    if (tenantDb.startsWith(prefix)) {
      return tenantDb.substring(prefix.length)
    }
    const parts = tenantDb.split('_')
    return parts.length > 1 ? parts[1] : tenantDb
  }

  const fetchCartCount = async () => {
    if (!customer) return
    const buyerMemNo = customer.memberNo
    const buyerMgCode = getMgCode(customer.tenantDb)
    if (!buyerMemNo || !buyerMgCode) return
    
    try {
      const response = await apiService.getKataleCart(buyerMemNo, buyerMgCode)
      if (response.status === 'SUCCESS' && response.data) {
        setCartCount(response.data.length || 0)
      }
    } catch (err) {
      console.log('Could not fetch cart:', err)
    }
  }

  const fetchProducts = async () => {
    setLoading(true)
    setError('')
    
    try {
      const response = await apiService.searchKataleProducts({
        search: searchQuery,
        categoryId: selectedCategory || null,
        sortBy: sortBy,
        page: currentPage,
        limit: 12
      })

      if (response.status === 'SUCCESS' && response.data) {
        setProducts(response.data.products || [])
        setTotalPages(response.data.pagination?.total_pages || 1)
        setTotalProducts(response.data.pagination?.total || 0)
        if (response.data.filters?.price_range) {
          setPriceRange(response.data.filters.price_range)
        }
      } else {
        setError(response.message || 'Failed to load products')
      }
    } catch (err) {
      console.error('Failed to fetch products:', err)
      setError('Failed to load products. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchProducts()
  }

  const handleAddToCart = async (productId) => {
    if (!customer) {
      alert('Please log in to add items to cart')
      return
    }
    
    const buyerMemNo = customer.memberNo
    const buyerMgCode = getMgCode(customer.tenantDb)
    
    try {
      const response = await apiService.addToKataleCart(productId, buyerMemNo, buyerMgCode, 1)
      if (response.status === 'SUCCESS') {
        setCartCount(prev => prev + 1)
        alert('Product added to cart!')
      } else {
        alert(response.message || 'Failed to add to cart')
      }
    } catch (err) {
      console.error('Add to cart error:', err)
      alert('Failed to add product to cart')
    }
  }

  if (loading && products.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/katale" className="text-gray-600 hover:text-gray-900 mr-4">
                <ChevronLeft className="h-5 w-5" />
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">Browse Products</h1>
            </div>
            <Link to="/katale/cart" className="btn btn-primary">
              <ShoppingCart className="h-5 w-5 mr-2" />
              Cart ({cartCount})
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
            <button onClick={fetchProducts} className="ml-auto text-red-700 hover:text-red-900 underline">
              Retry
            </button>
          </div>
        )}

        {/* Filters & Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest First</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="popular">Most Popular</option>
            </select>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <Grid className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List className="h-5 w-5" />
              </button>
            </div>
          </form>
        </div>

        {/* Results count */}
        <div className="mb-4 text-sm text-gray-600">
          Showing {products.length} of {totalProducts} products
          {loading && <span className="ml-2 text-blue-600">(Loading...)</span>}
        </div>

        {/* Products Grid/List */}
        {products.length === 0 && !loading ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No products found. Try adjusting your search or filters.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-48 bg-gray-200 flex items-center justify-center">
                  {product.primary_image ? (
                    <img src={product.primary_image} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-gray-400 text-center">
                      <Package className="h-12 w-12 mx-auto mb-2" />
                      <span className="text-sm">No Image</span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full mb-2">
                    {product.category_name || 'Uncategorized'}
                  </span>
                  <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
                  <p className="text-sm text-gray-500 mb-1">by {product.seller_name || 'Unknown'}</p>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-bold text-blue-600">{formatCurrency(product.price)}</span>
                    <span className="text-xs text-gray-500">{product.quantity} in stock</span>
                  </div>
                  <button
                    onClick={() => handleAddToCart(product.id)}
                    disabled={product.quantity <= 0}
                    className="w-full btn btn-primary btn-sm disabled:opacity-50"
                  >
                    <ShoppingCart className="h-4 w-4 mr-1" />
                    {product.quantity > 0 ? 'Add to Cart' : 'Out of Stock'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {products.map((product) => (
              <div key={product.id} className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-4">
                  <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    {product.primary_image ? (
                      <img src={product.primary_image} alt={product.name} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <Package className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full mb-1">
                          {product.category_name || 'Uncategorized'}
                        </span>
                        <h3 className="font-semibold text-gray-900">{product.name}</h3>
                        <p className="text-sm text-gray-500">by {product.seller_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-400">{product.quantity} available</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xl font-bold text-blue-600">{formatCurrency(product.price)}</span>
                        <button
                          onClick={() => handleAddToCart(product.id)}
                          disabled={product.quantity <= 0}
                          className="block mt-2 btn btn-primary btn-sm disabled:opacity-50"
                        >
                          <ShoppingCart className="h-4 w-4 mr-1" />
                          {product.quantity > 0 ? 'Add to Cart' : 'Out of Stock'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="mt-8 flex items-center justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-4 py-2 rounded-lg ${
                currentPage === i + 1
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default KataleProducts
